import React, { useState, useEffect, useRef } from 'react';

const ContentPanel = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  const sections = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'homepage', name: 'Homepage Editor', icon: '🏠' },
    { id: 'programs', name: 'Programs', icon: '📻' },
    { id: 'news', name: 'News Articles', icon: '📰' },
    { id: 'staff', name: 'Staff/Team', icon: '👥' },
    { id: 'gallery', name: 'Media Gallery', icon: '🖼️' },
    { id: 'about', name: 'About & Contact', icon: '📝' },
    { id: 'donations', name: 'Donations Page', icon: '💝' }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <CPanelDashboard backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'homepage':
        return <HomepageEditor backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'programs':
        return <ProgramsManager backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'news':
        return <NewsManager backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'staff':
        return <StaffManager backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'gallery':
        return <MediaGallery backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'about':
        return <AboutContactEditor backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      case 'donations':
        return <DonationsPageEditor backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
      default:
        return <CPanelDashboard backendUrl={BACKEND_URL} crmAuth={crmAuth} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white p-6">
        <h1 className="text-3xl font-bold">🖥️ Website Control Panel</h1>
        <p className="text-emerald-100 mt-1">Manage all Kioo Radio website content</p>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-lg min-h-screen">
          <nav className="p-4">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 text-left transition-all ${
                  activeSection === section.id
                    ? 'bg-emerald-100 text-emerald-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="text-xl">{section.icon}</span>
                <span>{section.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

// Dashboard Component
const CPanelDashboard = ({ backendUrl, crmAuth }) => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/stats`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Content Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon="📻" title="Programs" value={stats.programs || 0} color="blue" />
        <StatCard icon="📰" title="News Articles" value={stats.news || 0} color="green" />
        <StatCard icon="👥" title="Staff Members" value={stats.staff || 0} color="purple" />
        <StatCard icon="🖼️" title="Media Files" value={stats.media || 0} color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 Recent Updates</h3>
          {(stats.recentUpdates || []).length === 0 ? (
            <p className="text-gray-500 text-center py-4">No recent updates</p>
          ) : (
            <ul className="space-y-3">
              {(stats.recentUpdates || []).slice(0, 5).map((update, idx) => (
                <li key={idx} className="flex items-center gap-3 text-sm">
                  <span className="text-lg">{update.icon || '📄'}</span>
                  <span className="flex-1 text-gray-700">{update.title}</span>
                  <span className="text-gray-400 text-xs">{update.date}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <QuickAction icon="📰" label="New Article" onClick={() => {}} />
            <QuickAction icon="📻" label="Add Program" onClick={() => {}} />
            <QuickAction icon="🖼️" label="Upload Media" onClick={() => {}} />
            <QuickAction icon="👤" label="Add Staff" onClick={() => {}} />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className={`w-12 h-12 ${colors[color]} rounded-lg flex items-center justify-center text-2xl mb-3`}>
        {icon}
      </div>
      <p className="text-gray-500 text-sm">{title}</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

const QuickAction = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-sm font-medium"
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

// Homepage Editor Component
const HomepageEditor = ({ backendUrl, crmAuth }) => {
  const [content, setContent] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroButtonText: '',
    heroButtonLink: '',
    aboutTitle: '',
    aboutText: '',
    featuredPrograms: [],
    announcements: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/homepage`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data.content || content);
      }
    } catch (error) {
      console.error('Error fetching homepage content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/homepage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        alert('Homepage content saved successfully!');
      } else {
        alert('Error saving content');
      }
    } catch (error) {
      alert('Error saving content: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">🏠 Homepage Editor</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Hero Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 Hero Banner</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
              <input
                type="text"
                value={content.heroTitle}
                onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                placeholder="Welcome to Kioo Radio"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={content.heroSubtitle}
                onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                placeholder="Your Community Voice"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
              <input
                type="text"
                value={content.heroButtonText}
                onChange={(e) => setContent({ ...content, heroButtonText: e.target.value })}
                placeholder="Listen Now"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
              <input
                type="text"
                value={content.heroButtonLink}
                onChange={(e) => setContent({ ...content, heroButtonLink: e.target.value })}
                placeholder="/listen"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 About Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
              <input
                type="text"
                value={content.aboutTitle}
                onChange={(e) => setContent({ ...content, aboutTitle: e.target.value })}
                placeholder="About Kioo Radio"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">About Text</label>
              <textarea
                value={content.aboutText}
                onChange={(e) => setContent({ ...content, aboutText: e.target.value })}
                rows="4"
                placeholder="Kioo Radio 98.1 FM broadcasts from Betche Hill..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* Announcements */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📢 Announcements Banner</h3>
          <AnnouncementsEditor 
            announcements={content.announcements || []}
            onChange={(announcements) => setContent({ ...content, announcements })}
          />
        </div>
      </div>
    </div>
  );
};

const AnnouncementsEditor = ({ announcements, onChange }) => {
  const [newAnnouncement, setNewAnnouncement] = useState('');

  const addAnnouncement = () => {
    if (newAnnouncement.trim()) {
      onChange([...announcements, { id: Date.now(), text: newAnnouncement, active: true }]);
      setNewAnnouncement('');
    }
  };

  const removeAnnouncement = (id) => {
    onChange(announcements.filter(a => a.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={newAnnouncement}
          onChange={(e) => setNewAnnouncement(e.target.value)}
          placeholder="Enter announcement text..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          onKeyPress={(e) => e.key === 'Enter' && addAnnouncement()}
        />
        <button
          onClick={addAnnouncement}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          Add
        </button>
      </div>
      {announcements.map((announcement) => (
        <div key={announcement.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <span className="flex-1 text-gray-700">{announcement.text}</span>
          <button
            onClick={() => removeAnnouncement(announcement.id)}
            className="p-1 text-red-500 hover:bg-red-50 rounded"
          >
            🗑️
          </button>
        </div>
      ))}
    </div>
  );
};

// Programs Manager Component
const ProgramsManager = ({ backendUrl, crmAuth }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDay, setFilterDay] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/programs`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique days and categories for filters
  const days = ['all', ...new Set(programs.map(p => p.day_of_week).filter(Boolean))];
  const categories = ['all', ...new Set(programs.map(p => p.category).filter(Boolean))];

  // Filter programs
  const filteredPrograms = programs.filter(p => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.host?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDay = filterDay === 'all' || p.day_of_week === filterDay;
    const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchesSearch && matchesDay && matchesCategory;
  });

  const handleSaveProgram = async (programData) => {
    try {
      const url = editingProgram 
        ? `${backendUrl}/api/cpanel/programs/${editingProgram.id}`
        : `${backendUrl}/api/cpanel/programs`;
      
      const response = await fetch(url, {
        method: editingProgram ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(programData)
      });
      
      if (response.ok) {
        fetchPrograms();
        setShowModal(false);
        setEditingProgram(null);
      }
    } catch (error) {
      alert('Error saving program');
    }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Delete this program?')) return;
    
    try {
      await fetch(`${backendUrl}/api/cpanel/programs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      fetchPrograms();
    } catch (error) {
      alert('Error deleting program');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📻 Programs Manager</h2>
          <p className="text-sm text-gray-500">{programs.length} total programs • {filteredPrograms.length} showing</p>
        </div>
        <button
          onClick={() => { setEditingProgram(null); setShowModal(true); }}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          ➕ Add Program
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search programs by name, host, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            value={filterDay}
            onChange={(e) => setFilterDay(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Days</option>
            {days.filter(d => d !== 'all').map(day => (
              <option key={day} value={day}>{day?.charAt(0).toUpperCase() + day?.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Categories</option>
            {categories.filter(c => c !== 'all').map(cat => (
              <option key={cat} value={cat}>{cat?.charAt(0).toUpperCase() + cat?.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredPrograms.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <span className="text-4xl">📻</span>
          <p className="text-gray-500 mt-2">No programs found matching your criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrograms.slice(0, 50).map(program => (
          <div key={program.id} className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{program.name}</h3>
                <p className="text-sm text-gray-500">{program.host}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${program.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {program.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">{program.description?.slice(0, 100)}...</p>
            <p className="text-xs text-gray-400 mb-3">📅 {program.schedule}</p>
            <div className="flex gap-2">
              <button
                onClick={() => { setEditingProgram(program); setShowModal(true); }}
                className="flex-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => handleDeleteProgram(program.id)}
                className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
        </div>
      )}
      
      {filteredPrograms.length > 50 && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Showing first 50 programs. Use search/filters to find specific programs.
        </div>
      )}

      {showModal && (
        <ProgramModal
          program={editingProgram}
          onSave={handleSaveProgram}
          onClose={() => { setShowModal(false); setEditingProgram(null); }}
        />
      )}
    </div>
  );
};

const ProgramModal = ({ program, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: program?.name || '',
    host: program?.host || '',
    description: program?.description || '',
    schedule: program?.schedule || '',
    category: program?.category || 'general',
    is_active: program?.is_active ?? true
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 mx-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {program ? '✏️ Edit Program' : '➕ Add Program'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Program Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
            <input
              type="text"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
            <input
              type="text"
              value={formData.schedule}
              onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
              placeholder="e.g., Mon-Fri 8:00 AM"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-emerald-600"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active on website</label>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => onSave(formData)} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// News Manager Component
const NewsManager = ({ backendUrl, crmAuth }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/news`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setArticles(data.articles || []);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveArticle = async (articleData) => {
    try {
      const url = editingArticle 
        ? `${backendUrl}/api/cpanel/news/${editingArticle.id}`
        : `${backendUrl}/api/cpanel/news`;
      
      const response = await fetch(url, {
        method: editingArticle ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(articleData)
      });
      
      if (response.ok) {
        fetchArticles();
        setShowModal(false);
        setEditingArticle(null);
      }
    } catch (error) {
      alert('Error saving article');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    
    try {
      await fetch(`${backendUrl}/api/cpanel/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      fetchArticles();
    } catch (error) {
      alert('Error deleting article');
    }
  };

  const togglePublish = async (article) => {
    try {
      await fetch(`${backendUrl}/api/cpanel/news/${article.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...article, is_published: !article.is_published })
      });
      fetchArticles();
    } catch (error) {
      alert('Error updating article');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">📰 News Manager</h2>
        <button
          onClick={() => { setEditingArticle(null); setShowModal(true); }}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          ➕ New Article
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Title</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Category</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {articles.map(article => (
              <tr key={article.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{article.title}</p>
                  <p className="text-sm text-gray-500">{article.excerpt?.slice(0, 50)}...</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{article.category}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {new Date(article.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => togglePublish(article)}
                    className={`px-2 py-1 text-xs rounded-full ${
                      article.is_published 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {article.is_published ? '✓ Published' : 'Draft'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => { setEditingArticle(article); setShowModal(true); }}
                    className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded mr-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteArticle(article.id)}
                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <NewsModal
          article={editingArticle}
          onSave={handleSaveArticle}
          onClose={() => { setShowModal(false); setEditingArticle(null); }}
        />
      )}
    </div>
  );
};

const NewsModal = ({ article, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    category: article?.category || 'general',
    image_url: article?.image_url || '',
    is_published: article?.is_published ?? false
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {article ? '✏️ Edit Article' : '➕ New Article'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt (Short Description)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows="8"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="general">General</option>
                <option value="community">Community</option>
                <option value="programs">Programs</option>
                <option value="events">Events</option>
                <option value="announcements">Announcements</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 text-emerald-600"
            />
            <label htmlFor="is_published" className="text-sm text-gray-700">Publish immediately</label>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => onSave(formData)} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
            {formData.is_published ? 'Publish' : 'Save Draft'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Staff Manager Component
const StaffManager = ({ backendUrl, crmAuth }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/staff`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStaff = async (staffData) => {
    try {
      const url = editingStaff 
        ? `${backendUrl}/api/cpanel/staff/${editingStaff.id}`
        : `${backendUrl}/api/cpanel/staff`;
      
      const response = await fetch(url, {
        method: editingStaff ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(staffData)
      });
      
      if (response.ok) {
        fetchStaff();
        setShowModal(false);
        setEditingStaff(null);
      }
    } catch (error) {
      alert('Error saving staff');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    
    try {
      await fetch(`${backendUrl}/api/cpanel/staff/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      fetchStaff();
    } catch (error) {
      alert('Error deleting staff');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">👥 Staff/Team Manager</h2>
        <button
          onClick={() => { setEditingStaff(null); setShowModal(true); }}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          ➕ Add Staff
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map(member => (
          <div key={member.id} className="bg-white rounded-xl shadow-sm p-5 text-center">
            <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden bg-gray-200">
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl text-gray-400">👤</div>
              )}
            </div>
            <h3 className="font-semibold text-gray-900">{member.name}</h3>
            <p className="text-sm text-emerald-600">{member.role}</p>
            <p className="text-xs text-gray-500 mt-2">{member.bio?.slice(0, 80)}...</p>
            <div className="flex gap-2 mt-4 justify-center">
              <button
                onClick={() => { setEditingStaff(member); setShowModal(true); }}
                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => handleDeleteStaff(member.id)}
                className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <StaffModal
          staff={editingStaff}
          onSave={handleSaveStaff}
          onClose={() => { setShowModal(false); setEditingStaff(null); }}
        />
      )}
    </div>
  );
};

const StaffModal = ({ staff, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    role: staff?.role || '',
    bio: staff?.bio || '',
    photo_url: staff?.photo_url || '',
    email: staff?.email || '',
    phone: staff?.phone || '',
    is_active: staff?.is_active ?? true
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 mx-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {staff ? '✏️ Edit Staff' : '➕ Add Staff'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role/Title</label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="e.g., Program Host, Station Manager"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo URL</label>
            <input
              type="text"
              value={formData.photo_url}
              onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
              placeholder="https://..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="staff_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-emerald-600"
            />
            <label htmlFor="staff_active" className="text-sm text-gray-700">Show on website</label>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => onSave(formData)} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Media Gallery Component with Photo Editor
const MediaGallery = ({ backendUrl, crmAuth }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/media`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedia(data.media || []);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${crmAuth}` },
        body: formData
      });
      
      if (response.ok) {
        fetchMedia();
        alert('Upload successful!');
      } else {
        alert('Upload failed');
      }
    } catch (error) {
      alert('Upload error: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await fetch(`${backendUrl}/api/cpanel/media/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      fetchMedia();
    } catch (error) {
      alert('Error deleting file');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">🖼️ Media Gallery</h2>
        <div className="flex gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            multiple
            accept="image/*"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {uploading ? '⏳ Uploading...' : '📤 Upload Images'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {media.map(item => (
          <div key={item.id} className="relative group bg-white rounded-lg shadow-sm overflow-hidden">
            <img 
              src={item.url} 
              alt={item.name}
              className="w-full h-32 object-cover cursor-pointer"
              onClick={() => { setSelectedImage(item); setShowEditor(true); }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button
                onClick={() => { setSelectedImage(item); setShowEditor(true); }}
                className="p-2 bg-white rounded-full mr-2"
                title="Edit"
              >
                ✏️
              </button>
              <button
                onClick={() => handleDeleteMedia(item.id)}
                className="p-2 bg-white rounded-full"
                title="Delete"
              >
                🗑️
              </button>
            </div>
            <p className="p-2 text-xs text-gray-600 truncate">{item.name}</p>
          </div>
        ))}
      </div>

      {media.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl">
          <span className="text-5xl">🖼️</span>
          <p className="text-gray-500 mt-4">No media files yet. Upload some images!</p>
        </div>
      )}

      {showEditor && selectedImage && (
        <PhotoEditor
          image={selectedImage}
          backendUrl={backendUrl}
          crmAuth={crmAuth}
          onClose={() => { setShowEditor(false); setSelectedImage(null); }}
          onSave={() => { fetchMedia(); setShowEditor(false); setSelectedImage(null); }}
        />
      )}
    </div>
  );
};

// Photo Editor Component
const PhotoEditor = ({ image, backendUrl, crmAuth, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
    grayscale: 0,
    sepia: 0
  });
  const [rotation, setRotation] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadImage();
  }, [image, filters, rotation]);

  const loadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx.filter = `
        brightness(${filters.brightness}%)
        contrast(${filters.contrast}%)
        saturate(${filters.saturation}%)
        blur(${filters.blur}px)
        grayscale(${filters.grayscale}%)
        sepia(${filters.sepia}%)
      `;
      
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();
    };
    img.src = image.url;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      const response = await fetch(`${backendUrl}/api/cpanel/media/${image.id}/edit`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_data: dataUrl, filters, rotation })
      });
      
      if (response.ok) {
        alert('Image saved successfully!');
        onSave();
      } else {
        alert('Error saving image');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      blur: 0,
      grayscale: 0,
      sepia: 0
    });
    setRotation(0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex z-50">
      {/* Editor Canvas */}
      <div className="flex-1 flex items-center justify-center p-4">
        <canvas 
          ref={canvasRef}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
      </div>
      
      {/* Controls Panel */}
      <div className="w-80 bg-white p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">🎨 Photo Editor</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <div className="space-y-4">
          {/* Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ☀️ Brightness: {filters.brightness}%
            </label>
            <input
              type="range"
              min="0"
              max="200"
              value={filters.brightness}
              onChange={(e) => setFilters({ ...filters, brightness: e.target.value })}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🎚️ Contrast: {filters.contrast}%
            </label>
            <input
              type="range"
              min="0"
              max="200"
              value={filters.contrast}
              onChange={(e) => setFilters({ ...filters, contrast: e.target.value })}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🌈 Saturation: {filters.saturation}%
            </label>
            <input
              type="range"
              min="0"
              max="200"
              value={filters.saturation}
              onChange={(e) => setFilters({ ...filters, saturation: e.target.value })}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              💨 Blur: {filters.blur}px
            </label>
            <input
              type="range"
              min="0"
              max="10"
              value={filters.blur}
              onChange={(e) => setFilters({ ...filters, blur: e.target.value })}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ⬛ Grayscale: {filters.grayscale}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={filters.grayscale}
              onChange={(e) => setFilters({ ...filters, grayscale: e.target.value })}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🟤 Sepia: {filters.sepia}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={filters.sepia}
              onChange={(e) => setFilters({ ...filters, sepia: e.target.value })}
              className="w-full"
            />
          </div>
          
          {/* Rotation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔄 Rotation: {rotation}°
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setRotation((r) => r - 90)}
                className="flex-1 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                ↺ -90°
              </button>
              <button
                onClick={() => setRotation((r) => r + 90)}
                className="flex-1 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                ↻ +90°
              </button>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="mt-8 space-y-3">
          <button
            onClick={resetFilters}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            🔄 Reset All
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? '💾 Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// About & Contact Editor
const AboutContactEditor = ({ backendUrl, crmAuth }) => {
  const [content, setContent] = useState({
    aboutTitle: '',
    aboutContent: '',
    mission: '',
    vision: '',
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    socialFacebook: '',
    socialTwitter: '',
    socialInstagram: '',
    socialYoutube: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/about`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data.content || content);
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/about`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        alert('Content saved successfully!');
      }
    } catch (error) {
      alert('Error saving content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">📝 About & Contact Editor</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>
      </div>

      <div className="space-y-6">
        {/* About Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📖 About Page</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
              <input
                type="text"
                value={content.aboutTitle}
                onChange={(e) => setContent({ ...content, aboutTitle: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">About Content</label>
              <textarea
                value={content.aboutContent}
                onChange={(e) => setContent({ ...content, aboutContent: e.target.value })}
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mission</label>
                <textarea
                  value={content.mission}
                  onChange={(e) => setContent({ ...content, mission: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vision</label>
                <textarea
                  value={content.vision}
                  onChange={(e) => setContent({ ...content, vision: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📞 Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={content.contactEmail}
                onChange={(e) => setContent({ ...content, contactEmail: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={content.contactPhone}
                onChange={(e) => setContent({ ...content, contactPhone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                value={content.contactAddress}
                onChange={(e) => setContent({ ...content, contactAddress: e.target.value })}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🌐 Social Media Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">📘 Facebook</label>
              <input
                type="url"
                value={content.socialFacebook}
                onChange={(e) => setContent({ ...content, socialFacebook: e.target.value })}
                placeholder="https://facebook.com/..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🐦 Twitter/X</label>
              <input
                type="url"
                value={content.socialTwitter}
                onChange={(e) => setContent({ ...content, socialTwitter: e.target.value })}
                placeholder="https://twitter.com/..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">📸 Instagram</label>
              <input
                type="url"
                value={content.socialInstagram}
                onChange={(e) => setContent({ ...content, socialInstagram: e.target.value })}
                placeholder="https://instagram.com/..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">📺 YouTube</label>
              <input
                type="url"
                value={content.socialYoutube}
                onChange={(e) => setContent({ ...content, socialYoutube: e.target.value })}
                placeholder="https://youtube.com/..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Donations Page Editor
const DonationsPageEditor = ({ backendUrl, crmAuth }) => {
  const [content, setContent] = useState({
    pageTitle: '',
    introText: '',
    impactText: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    mobileMoneyNumbers: [],
    paypalEmail: '',
    cryptoAddresses: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/donations`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data.content || content);
      }
    } catch (error) {
      console.error('Error fetching donations content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/cpanel/donations`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        alert('Donations page saved successfully!');
      }
    } catch (error) {
      alert('Error saving content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-pulse">⏳</span></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">💝 Donations Page Editor</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Page Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📄 Page Content</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
              <input
                type="text"
                value={content.pageTitle}
                onChange={(e) => setContent({ ...content, pageTitle: e.target.value })}
                placeholder="Support Kioo Radio"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Introduction Text</label>
              <textarea
                value={content.introText}
                onChange={(e) => setContent({ ...content, introText: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact Statement</label>
              <textarea
                value={content.impactText}
                onChange={(e) => setContent({ ...content, impactText: e.target.value })}
                rows="3"
                placeholder="Your donation helps us..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏦 Bank Transfer Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input
                type="text"
                value={content.bankName}
                onChange={(e) => setContent({ ...content, bankName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
              <input
                type="text"
                value={content.accountName}
                onChange={(e) => setContent({ ...content, accountName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input
                type="text"
                value={content.accountNumber}
                onChange={(e) => setContent({ ...content, accountNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
              <input
                type="text"
                value={content.routingNumber}
                onChange={(e) => setContent({ ...content, routingNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SWIFT Code</label>
              <input
                type="text"
                value={content.swiftCode}
                onChange={(e) => setContent({ ...content, swiftCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Email</label>
              <input
                type="email"
                value={content.paypalEmail}
                onChange={(e) => setContent({ ...content, paypalEmail: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentPanel;
